import {
    IAccountMappingTemplate,
    IAccountMappingTemplateGroup,
    IAccountMappingTemplateGroupItemFLI,
    ISuperTableRow,
    ItemType,
    SuperTableRowType,
} from '@modeso/types__aumico-lib-annualreport-be';
import { IXAxisConfigColumn, SuperTableColumnType } from '@modeso/types__aumico-lib-notes-be';
import { TemplateType } from '@modeso/types__aumico-lib-templates-be';
import { cloneDeep } from 'lodash-es';
import { EMPTY_ARRAY_LENGTH, NumberUtils } from '../../../../aumico-lib-core-fe/src/lib';
import { isFli, isSubfli } from '../../../../aumico-lib-templates-fe/src/lib';
import { FX_RESERVE_NODE_NAME } from '../interfaces/IFxNotesContext.interface';
import { MappingItemsSelectionNode } from '../interfaces/mapping-items-selection-node.interface';
import { SuperTableMapper } from '../mappers/super-table.mapper';

const superTableMapper = new SuperTableMapper();

// Function to map FLIs by finding them in allFlis based on caption
export function findFlis(ids: string[], allFlis) {
    return ids
        .map((fliId) => allFlis.find((allFli) => allFli.name === fliId))
        .filter(Boolean)
        .map((fli) => fli.name);
}

export function findFlisById(ids: string[], allFlis) {
    return ids
        .map((fliId) => allFlis.find((allFli) => allFli.id === fliId))
        .filter(Boolean)
        .map((fli) => fli.id);
}

// Function to map FLIs from the source structure to the target structure
export function mapFlis(flisArray, allFlis, balance, income) {
    return flisArray
        .map((fli) => {
            const matchingFli = allFlis.find((allFli) => allFli.caption === fli.caption);
            if (!matchingFli) return null;

            const balanceFli = allFlis.find((allFli) => allFli.caption === fli.caption);
            let belongingTemplateType = TemplateType.BALANCE;
            if (!balanceFli) {
                belongingTemplateType = TemplateType.INCOME;
            }
            // Transforming the matching FLI to the desired target structure
            return {
                id: matchingFli.name,
                caption: matchingFli.caption,
                position: matchingFli.rowNo || 0, // Assuming `rowNo` could be used for position
                accounts: matchingFli.accounts.flatMap((account) => {
                    if (isSubfli(account)) {
                        return account.accounts.map((subAccount) => ({
                            accountName: subAccount.name,
                            id: subAccount.accountNumber,
                            previousValue: subAccount.calculatedOpeningBalance,
                            currentValue: subAccount.calculatedClosingBalance,
                        }));
                    } else {
                        return {
                            accountName: account.name,
                            id: account.accountNumber,
                            previousValue: account.calculatedOpeningBalance,
                            currentValue: account.calculatedClosingBalance,
                        };
                    }
                }),
                totalRow: {
                    accountName: 'Total',
                    currentValue: matchingFli.calculatedClosingBalance,
                    previousValue: matchingFli.calculatedOpeningBalance,
                },
                type: matchingFli.fliType,
                itemType: 'FLI',
                belongingTemplateType: belongingTemplateType,
                addNumbering: fli.addNumbering ?? false,
            };
        })
        .filter(Boolean); // Filters out any null results (non-matching FLIs)
}
export function mapFlisForMovement(flisArray, allFlis, balance, income, componentData, locale) {
    return flisArray
        .map((fli) => {
            const caption = fli?.caption;
            const localizedCaption =
                caption == null
                    ? null
                    : typeof caption === 'string'
                        ? caption
                        : caption[locale] || caption['de-ch'];

            if (!localizedCaption) {
                return null;
            }

            const matchingFli = allFlis.find((allFli) => allFli.caption === localizedCaption);
            if (!matchingFli) return null;

            const balanceFli = balance.find((balanceFli) => balanceFli.caption === localizedCaption);
            let belongingTemplateType = TemplateType.BALANCE;
            if (!balanceFli) {
                belongingTemplateType = TemplateType.INCOME;
            }

            // Transforming the matching FLI to the desired target structure
            return {
                id: matchingFli.name,
                caption: fli.caption,
                headers: componentData.headerOptions,
                position: matchingFli.rowNo || 0, // Assuming `rowNo` can be used for position
                totalRow: {
                    closeValue: matchingFli.calculatedClosingBalance || 0,
                    openValue: matchingFli.calculatedOpeningBalance || 0,
                    column1: 0, // Placeholder values, update with real calculations if needed
                    column2: 0,
                    column3: 0,
                    column4: 0,
                    text: fli.totalRow.text,
                },
                accounts: matchingFli.accounts.flatMap((account) => {
                    if (isSubfli(account)) {
                        return account.accounts.map((subAccount) => ({
                            id: subAccount.accountNumber,
                            text: subAccount.name,
                            closeValue: subAccount.calculatedClosingBalance || 0,
                            openValue: subAccount.calculatedOpeningBalance || 0,
                            column1: 0, // Placeholder values, update with real calculations if needed
                            column2: 0,
                            column3: 0,
                            column4: 0,
                        }));
                    }

                    return {
                        id: account.accountNumber,
                        text: account.name,
                        closeValue: account.calculatedClosingBalance || 0,
                        openValue: account.calculatedOpeningBalance || 0,
                        column1: 0, // Placeholder values, update with real calculations if needed
                        column2: 0,
                        column3: 0,
                        column4: 0,
                    };
                }),

                templateType: belongingTemplateType,
                itemType: 'FLI',
                addNumbering: fli.addNumbering ?? false,
            };
        })
        .filter(Boolean); // Filters out any null results (non-matching FLIs)
}

export function getAllFlis(group: IAccountMappingTemplateGroup): IAccountMappingTemplateGroupItemFLI[] {
    const flis: IAccountMappingTemplateGroupItemFLI[] = [];
    for (const item of group.items) {
        if (isFli(item)) {
            flis.push(item);
        }
    }
    for (const innerGroup of group.groups) {
        flis.push(...getAllFlis(innerGroup));
    }
    return flis;
}

// Function to calculate the total row based on rows data
export function calculateTotalRow(rows, totalText) {
    return rows.reduce(
        (total, row) => ({
            text: total.text,
            currentValue1: total.currentValue1 + (row.currentValue1 || 0),
            previousValue1: total.previousValue1 + (row.previousValue1 || 0),
            currentValue2: total.currentValue2 + (row.currentValue2 || 0),
            previousValue2: total.previousValue2 + (row.previousValue2 || 0),
        }),
        { text: totalText, currentValue1: 0, previousValue1: 0, currentValue2: 0, previousValue2: 0 },
    );
}

// Main function to create a component copy with mapped FLIs and calculated totals
export function getTotalAndRows(component, totalText) {
    const totalRow = calculateTotalRow(component.data.rows, totalText);

    return [component.data.rows, totalRow];
}

export function getSuperTableCopiedRows(
    rowsToCopy: ISuperTableRow[],
    columns: IXAxisConfigColumn[],
    balanceMappingTemplate: IAccountMappingTemplate,
    incomeMappingTemplate: IAccountMappingTemplate,
): ISuperTableRow[] {
    const copiedRows: ISuperTableRow[] = [];
    const balanceMappingItemsSelectionTree: MappingItemsSelectionNode = cloneDeep<MappingItemsSelectionNode>(
        superTableMapper.buildMappingItemsSelectionTree(balanceMappingTemplate.group, TemplateType.BALANCE),
    );
    const incomeMappingItemsSelectionTree: MappingItemsSelectionNode = cloneDeep<MappingItemsSelectionNode>(
        superTableMapper.buildMappingItemsSelectionTree(incomeMappingTemplate.group, TemplateType.INCOME),
    );

    const allMappingItemsSelectionTree: MappingItemsSelectionNode = {
        data: null,
        children: balanceMappingItemsSelectionTree.children.concat(incomeMappingItemsSelectionTree),
        type: ItemType.GROUP,
        financialType: null,
        expanded: true,
    };

    rowsToCopy.forEach((row) => {
        const matchingNodes: MappingItemsSelectionNode[] = [];

        // FX (REV-21512): a rate row references ANY node kind (its own type RATE exists on no
        // tree node), and the virtual reserve node is not part of the re-match trees at all —
        // its sentinel name is copy-stable by design (G1), so those ids travel verbatim.
        const matchType = row.type === SuperTableRowType.RATE ? null : row.type;
        const verbatimIds: string[] = [];
        for (const itemId of row.itemsIds) {
            if (itemId === FX_RESERVE_NODE_NAME) {
                verbatimIds.push(itemId);
                continue;
            }
            const matchingNode = superTableMapper.findMatchingNodeForRowItem(
                itemId,
                matchType,
                allMappingItemsSelectionTree,
            );

            if (matchingNode) {
                matchingNodes.push(matchingNode);
            }
        }

        const matchingNodesIds = [
            ...matchingNodes.map((node) => node.data?.accountNumber || node.data?.name),
            ...verbatimIds,
        ];

        let copiedRow: ISuperTableRow = {
            rowId: row.rowId,
            title: row.title,
            type: row.type,
            itemsIds: matchingNodesIds,
            // FX (REV-21512): rate rows never carry copied values — they re-resolve in the new
            // report (G1). The source travels, the rate does not.
            previousValue: row.type === SuperTableRowType.RATE ? undefined : row.previousValue,
            currentValue: row.type === SuperTableRowType.RATE ? undefined : row.currentValue,
            financialtype: row.financialtype,
            format: row.format,
            rateSource: row.rateSource,
        };

        if (row.reconcileItems != null) {
            copiedRow.reconcileItems = row.reconcileItems.map((reconcileItem) => {
                const matchingNode = superTableMapper.findMatchingNodeForReconcileItem(
                    reconcileItem,
                    allMappingItemsSelectionTree,
                );

                if (matchingNode != null) {
                    return {
                        itemName:
                            matchingNode.type === ItemType.ACCOUNT
                                ? matchingNode.data.accountNumber
                                : matchingNode.data.name,
                        type: matchingNode.type,
                        financialtype: matchingNode.financialType,
                    };
                }
                return reconcileItem;
            });
        }

        for (const column of columns) {
            // FX (REV-21512): rate rows carry no values in any column — everything re-resolves.
            if (copiedRow.type === SuperTableRowType.RATE) {
                continue;
            }
            const { currentValue, previousValue } = getValuesAccordingToItemsIds(
                copiedRow.itemsIds,
                allMappingItemsSelectionTree,
                copiedRow.type,
            );

            if (
                column.type === SuperTableColumnType.PREVIOUS_YEAR &&
                copiedRow.itemsIds.length !== EMPTY_ARRAY_LENGTH && copiedRow.type !== SuperTableRowType.TITLE && copiedRow.type !== SuperTableRowType.FREE_TEXT
            ) {
                copiedRow.previousValue = previousValue;
                continue;
            }

            if (column.type === SuperTableColumnType.PREVIOUS_YEAR && copiedRow.itemsIds.length === EMPTY_ARRAY_LENGTH && copiedRow.type !== SuperTableRowType.TITLE && copiedRow.type !== SuperTableRowType.FREE_TEXT) {
                copiedRow.previousValue = 0;
                continue
            }

            if (column.type === SuperTableColumnType.CURRENT_YEAR && copiedRow.itemsIds.length !== EMPTY_ARRAY_LENGTH && copiedRow.type !== SuperTableRowType.TITLE && copiedRow.type !== SuperTableRowType.FREE_TEXT) {
                copiedRow.currentValue = currentValue;
                continue;
            }

            if (column.type === SuperTableColumnType.CURRENT_YEAR && copiedRow.itemsIds.length === EMPTY_ARRAY_LENGTH && copiedRow.type !== SuperTableRowType.TITLE && copiedRow.type !== SuperTableRowType.FREE_TEXT) {
                copiedRow.currentValue = 0;
                continue;
            }

            if (column.type === SuperTableColumnType.CALCULATION && copiedRow.type !== SuperTableRowType.TITLE && copiedRow.type !== SuperTableRowType.FREE_TEXT) {
                // no need to copy calculation values since they will be recalculated in the super table form
                copiedRow[column.columnId] = 0;
                continue;
            }

            copiedRow[column.columnId] = row[column.columnId];
        }
        copiedRows.push(copiedRow);
    });
    return copiedRows;
}

function getValuesAccordingToItemsIds(
    itemsIds: string[],
    mappingItemsTree: MappingItemsSelectionNode,
    type: SuperTableRowType,
): { currentValue: number; previousValue: number } {
    let currentYearValues: number[] = [];
    let previousYearValues: number[] = [];

    for (const itemId of itemsIds) {
        const matchingNode = superTableMapper.findMatchingNodeForRowItem(itemId, type, mappingItemsTree);
        if (matchingNode != null) {
            currentYearValues.push(matchingNode.data.calculatedClosingBalance);
            previousYearValues.push(matchingNode.data.calculatedOpeningBalance);
        }
    }

    return {
        currentValue: NumberUtils.sumIgnoringInfiniteNumbers(currentYearValues, 0),
        previousValue: NumberUtils.sumIgnoringInfiniteNumbers(previousYearValues, 0),
    };
}
